'use client';

import { useCallback, useRef } from 'react';
import { BaseLineLeft } from '@/app/components/icons/BaseLineLeft';
import { useActivityInteraction, useCommunityInteraction } from '@/app/components/shared/Community';
import { FeedDetail } from '@/app/components/shared/FeedDetail';
import { HOME_PAGE } from '@/app/constants';
import { useFeedDetailNavigation, useFeedList } from '@/app/hooks';
import type { FeedDetailConfig, UpdateInteractionStatusParams, ViduV1ExploreDetailProReply } from '@/app/types';
import { type FeedDetailType, getActivityIdFromData } from '@/app/utils';
import { useRouter } from '@/i18n/routing';

interface DetailProps {
  data: ViduV1ExploreDetailProReply;
  /** Feed 详情类型，默认为社区 */
  feedType?: FeedDetailType;
}

export const Detail = ({ data: initialData, feedType = 'community' }: DetailProps) => {
  const router = useRouter();
  const isActivity = feedType === 'activity';
  const currentDataRef = useRef<ViduV1ExploreDetailProReply | null>(null);

  // 社区通用交互
  const { collect, unCollect, vote: communityVote, unVote: communityUnVote } = useCommunityInteraction({});

  // 活动专用交互
  const { vote: baseActivityVote, unVote: baseActivityUnVote } = useActivityInteraction();

  const { feedList, hasMore, isInitialLoading, handleLoadMore } = useFeedList();

  const {
    currentData,
    loading: detailLoading,
    handleThumbnailClick,
    getCurrentId,
    handleLeave,
  } = useFeedDetailNavigation({
    initialData,
    list: isActivity ? [] : feedList,
  });

  currentDataRef.current = currentData ?? null;

  // 活动点赞封装（从当前数据获取 activityId）
  const activityVote = useCallback(
    (params: UpdateInteractionStatusParams) => {
      const activityId = getActivityIdFromData(currentDataRef.current);
      if (activityId) {
        baseActivityVote({ ...params, activityId, isInspiration: false });
      }
    },
    [baseActivityVote],
  );

  const activityUnVote = useCallback(
    (params: UpdateInteractionStatusParams) => {
      const activityId = getActivityIdFromData(currentDataRef.current);
      if (activityId) {
        baseActivityUnVote({ ...params, activityId, isInspiration: false });
      }
    },
    [baseActivityUnVote],
  );

  const config: FeedDetailConfig = {
    source: 'share',
    ...(isActivity
      ? {
          handleVote: activityVote,
          handleUnVote: activityUnVote,
        }
      : {
          handleCollect: collect,
          handleUnCollect: unCollect,
          handleVote: communityVote,
          handleUnVote: communityUnVote,
        }),
    // 活动类型暂不显示缩略图列表
    thumbScrollListConfig: isActivity
      ? undefined
      : {
          list: feedList,
          currentId: getCurrentId(),
          onThumbnailClick: handleThumbnailClick,
          onLoadMore: handleLoadMore,
          hasMore,
          isLoading: detailLoading,
        },
  };

  return (
    <div className="relative h-screen w-full">
      <span
        className="absolute top-8 left-8 z-10 flex size-9 cursor-pointer items-center justify-center rounded-8 border border-system-white10 bg-system-white10 transition-all duration-300 hover:bg-system-white20 md:top-1.5 md:left-4 md:size-8 md:rounded-10 md:border-none md:bg-black/30 md:text-xl md:backdrop-blur-[32px]"
        onClick={() => {
          router.replace(HOME_PAGE);
          handleLeave();
        }}
      >
        <BaseLineLeft className="size-5 text-system-white" />
      </span>
      <FeedDetail data={currentData} config={config} loading={detailLoading || isInitialLoading} />
    </div>
  );
};
